import Papa from "papaparse";
import React, { useState } from "react";
import TkButton from "../../atoms/TkButton";
import { Box, Typography } from "@mui/material";
import {
  ITkCsvValidateObject,
  TkCsvUploadProps,
} from "../../../type/componentsType/TkCsvUpload";
import TkModalWithTitle from "../../atoms/TkModalWithTitle";
import TkTable from "../../atoms/TkTable";
import { transferResultFormat } from "./hooks/useTransferResult";
import {
  getFormatData,
  handleErrors,
  checkErrorBankResult,
} from "./hooks/useHook";
import { formatNonDeliverDetail } from "./hooks/useNonDeliveryDetail";
import "./_style.scss";
import {
  headListBefore,
  houjinStaffInfoValidate,
  houjinStaffMonthlySalaryValidate,
} from "./parts";
import TkPropertyMessageGet from "../../../utils/TkPropertyMessageGet";

/**
 * TkCsvUploadは、CSV一括アップロードを行うための部品です。
 * @param props - TkCsvUpload部品のプロパティ
 * @param props.buttonName - ボタンの名前
 * @param props.onSuccess - 成功時のコールバック関数
 * @param props.validate - バリデーション情報
 * @param props.type - ファイルの種類 houjinStaffInfo | houjinStaffMonthlySalary | transferResult | nonDeliveryDetail
 * @param props.disabled - ボタンの無効化
 * @param props.id - ボタンのID
 * @returns TkCsvUpload部品
 */
const TkCsvUpload: React.FC<TkCsvUploadProps> = (props) => {
  const {
    buttonName = "一括アップロード",
    onSuccess,
    type,
    disabled = false,
    id = "01",
  } = props;

  const [open, setOpen] = useState(false);
  const [errorList, setErrorList] = useState<any[]>([]);
  const [head, setHead] = useState<any[]>([]);
  const fileInputId = `fileInput${id}`;

  // ファイル選択時の処理
  const handleUpload = (e: any) => {
    if (!e.target.files) {
      return;
    }
    const file = e.target.files[0];
    const isDat = file?.name?.split(".")[1] === "dat";

    // ファイルタイプチェック
    const validMimeTypes = [
      "text/csv",
      "text/plain",
      "application/vnd.ms-excel",
      "application/csv",
    ];
    if (!isDat && !validMimeTypes.includes(file.type)) {
      setErrorList([
        [
          {
            children: 1,
          },
          {
            children: "TKMSG_SCR0081E",
          },
          {
            children: TkPropertyMessageGet("TKMSG_SCR0081E"),
          },
        ],
      ]);
      setHead(headListBefore);
      setOpen(true);
      return;
    }

    const validations: ITkCsvValidateObject = {
      houjinStaffInfo: houjinStaffInfoValidate,
      houjinStaffMonthlySalary: houjinStaffMonthlySalaryValidate,
    };

    Papa.parse(file, {
      download: false,
      skipEmptyLines: true,
      complete: function (results) {
        e.target.value = "";
        let validateResult = [];
        let errorList = [];
        let headList = [];

        switch (type) {
          case "houjinStaffInfo":
          case "houjinStaffMonthlySalary":
            ({ errorList, headList } = handleErrors(
              results.data,
              validations[type]
            ));
            break;
          case "transferResult":
            ({ errorList, headListBank: headList } = checkErrorBankResult(
              results.data,
              type
            ));
            break;
          case "nonDeliveryDetail":
            ({ errorList, headListBank: headList } = checkErrorBankResult(
              results.data,
              type
            ));
            break;
          default:
            return;
        }

        setErrorList(errorList);
        setHead(headList);

        if (errorList.length > 0) {
          setOpen(true);
          return;
        }

        switch (type) {
          case "houjinStaffInfo":
          case "houjinStaffMonthlySalary":
            validateResult = getFormatData(results.data, type);
            break;
          case "transferResult":
            validateResult = transferResultFormat(results.data);
            break;
          case "nonDeliveryDetail":
            validateResult = formatNonDeliverDetail(results.data);
            break;
        }

        onSuccess && onSuccess(validateResult);
      },
    });
  };

  const handleClose = () => {
    setOpen(false);
    clearSaveData();
  };

  const clearSaveData = () => {
    setErrorList([]);
  };

  return (
    <Box className="tkCsvUpload">
      <TkButton disabled={disabled}>
        <label htmlFor={fileInputId}>{buttonName}</label>
        <input
          id={fileInputId}
          className="fileUploadInput"
          type="file"
          accept=".csv, .txt, .dat"
          onChange={handleUpload}
        />
      </TkButton>
      <TkModalWithTitle
        open={open}
        title="ファイル取り込みエラー"
        className="tkCsvUploadModal"
        onClose={handleClose}
        clearSaveData={clearSaveData}
        cancelButtonName="閉じる"
        showSaveButton={false}
        data-testid="tkCsvUploadModalForTest"
      >
        <Box>
          <Typography className="subTitle">
            下記データにてエラーが発生しております。ご確認ください。
          </Typography>
          <Box className="tkCsvTable">
            <TkTable headList={head} bodyList={errorList} />
          </Box>
        </Box>
      </TkModalWithTitle>
    </Box>
  );
};
export default TkCsvUpload;
